import { ArrowLeft, Clock, Star } from 'lucide-react';
import { Link, useParams } from 'react-router';
import { errorMessage } from '@/api/client';
import { useConnections, useIntegrations } from '@/api/hooks';
import { Badge, Card, ErrorNote, IntegrationIcon, Skeleton } from '@/components/ui';
import { ConnectPanel } from '@/features/connect/ConnectPanel';
import { ConnectionStatusBadge } from '@/features/connections/ConnectionStatus';
import { NotFound } from '@/features/NotFound';
import { relativeTime } from '@/lib/format';
import { DemoBadge, StatusBadge } from './status';

const UNIT_HINT: Record<string, string> = {
  count: 'Number',
  currency: 'Money',
  percent: 'Percent',
  duration: 'Time',
  hours: 'Hours',
  text: 'Name',
};

export function IntegrationDetail() {
  const { integrationId = '' } = useParams();
  const q = useIntegrations();
  const connections = useConnections();
  const integration = q.data?.integrations.find((i) => i.id === integrationId);
  const category = q.data?.categories.find((c) => c.id === integration?.category);
  const mine = connections.data?.filter((c) => c.integration_id === integrationId) ?? [];

  if (q.isPending) return <Skeleton className="h-72" />;
  if (q.isError) return <ErrorNote message={errorMessage(q.error)} onRetry={() => void q.refetch()} />;
  if (!integration) return <NotFound />;

  return (
    <div className="mx-auto max-w-3xl">
      <Link to="/integrations" className="mb-4 inline-flex items-center gap-1 text-sm text-muted-ink hover:text-ink">
        <ArrowLeft className="size-4" aria-hidden /> All integrations
      </Link>
      <header className="flex flex-wrap items-start gap-4">
        <IntegrationIcon name={integration.name} size="lg" />
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-semibold tracking-[-0.02em]">{integration.name}</h1>
          <p className="text-sm text-muted-ink">
            {category?.name} · {integration.account_label}
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <StatusBadge status={integration.status} />
            {integration.status === 'beta' && <Badge tone="outline">Accuracy depends on how your boards are set up</Badge>}
          </div>
        </div>
      </header>

      <div className="mt-8 grid gap-6 md:grid-cols-[1fr_280px]">
        <Card as="section" className="p-5">
          <h2 className="font-semibold">{mine.length ? 'Connect another' : 'Connect'}</h2>
          <div className="mt-3">
            <ConnectPanel integration={integration} compact />
          </div>
        </Card>
        <Card as="section" className="p-5 text-sm">
          <h2 className="font-semibold">What you need</h2>
          <p className="mt-2 text-muted-ink">{integration.user_requirements}</p>
          {integration.today_note && (
            <p className="mt-3 flex gap-2 text-muted-ink">
              <Clock className="mt-0.5 size-4 shrink-0" aria-hidden />
              {integration.today_note}
            </p>
          )}
        </Card>
      </div>

      {mine.length > 0 && (
        <section aria-labelledby="your-connections" className="mt-8">
          <h2 id="your-connections" className="mb-3 font-semibold">
            Your {integration.name} connections
          </h2>
          <Card className="divide-y divide-border">
            {mine.map((c) => (
              <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm">
                <div className="min-w-0">
                  <p className="truncate font-medium">{c.display_name}</p>
                  <p className="text-xs text-muted-ink">Last synced {relativeTime(c.last_synced_at)}</p>
                </div>
                <div className="flex gap-1.5">
                  {c.is_demo && <DemoBadge />}
                  <ConnectionStatusBadge status={c.status} />
                </div>
              </div>
            ))}
          </Card>
        </section>
      )}

      <section aria-labelledby="kpis" className="mt-8">
        <h2 id="kpis" className="mb-3 font-semibold">
          Numbers you’ll see
        </h2>
        {integration.kpis.length === 0 ? (
          <Card className="p-4 text-sm text-muted-ink">One price card per ticker symbol you add.</Card>
        ) : (
          <Card as="div" className="divide-y divide-border">
            {integration.kpis.map((k) => (
              <div key={k.key} className="flex items-start justify-between gap-4 px-4 py-3 text-sm">
                <div className="min-w-0">
                  <p className="flex items-center gap-1.5 font-medium">
                    {k.label}
                    {k.default && <Star className="size-3.5 fill-yellow text-yellow" aria-label="Added automatically" />}
                  </p>
                  {k.description && <p className="text-xs text-muted-ink">{k.description}</p>}
                </div>
                <span className="shrink-0 text-xs text-muted-ink">{UNIT_HINT[k.unit] ?? k.unit}</span>
              </div>
            ))}
          </Card>
        )}
        {integration.kpis.some((k) => k.default) && (
          <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-ink">
            <Star className="size-3 fill-yellow text-yellow" aria-hidden /> Added to your dashboard automatically after the first sync.
          </p>
        )}
      </section>
    </div>
  );
}
